import React from 'react';
import { Calendar, FileSpreadsheet, ShieldCheck, Map, ClipboardCheck, ClipboardList, Biohazard, Building2 } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ModuleActivity } from '../../services/moduleActivity';
import { ModuleTile, ModuleStats, ActivityLine, SectionHeading, AblageKarte } from './ModuleTile';
import { KEINE_AKTIVITAET } from './moduleAccents';
import type { HubModulKennung } from '../../services/appUmfang';
import type { PlanungKachelStand } from '../planungsphase/usePlanungKachel';
import { PlanungKachelHinweis, PlanungKachelStatus } from '../planungsphase/PlanungAmpelAnzeige';
import { gruendeTooltip, lvHatPlanungsleistung, planungUntertitel } from '../planungsphase/planungKachel';
import type { BauordnungKachelAnzeige } from '../bauordnung/kachel';
import { BauordnungKachelStatus } from '../bauordnung/BauordnungKachelStatus';

/* Die Kachel-Bausteine (ModuleTile, ACCENTS, ActivityLine, SectionHeading,
   Ablage-Karte) liegen in ModuleTile.tsx: das SiFa-Raster (SifaModuleGrid)
   zeichnet dieselben Kacheln, und zwei Abschriften liefen auseinander. */

/* 'handbuch' und 'koordinierung' fehlen hier bewusst: beide sind Reiter im
   SiGe-Plan und werden nicht mehr aus dem Raster heraus angesprungen. Die
   Modulschluessel selbst gibt es in App.executeModuleNavigation weiter — das
   Handbuch springt darueber auf die Gewerke-Ueberschneidung. */
type ModuleTarget = 'editor' | 'vorank' | 'unterlage' | 'module-sigeko-inspection' | 'project-map' | 'module-archive' | 'module-sent-reports' | 'sigeplan' | 'einweisung' | 'dokumente' | 'planung' | 'asplan' | 'bauordnung';

/** Die Kacheln des Projektverlaufs am Rechner, fuer die Frage „bleibt der Abschnitt leer?". */
const VERLAUF_KACHELN: readonly HubModulKennung[] = ['planung', 'vorank', 'sigeplan', 'asplan', 'einweisung', 'unterlage', 'bauordnung', 'module-sigeko-inspection'];
/** Auf dem Handy steht davon nur die Einweisung im Raster (die Begehung als Knopf im Hub). */
const VERLAUF_KACHELN_MOBIL: readonly HubModulKennung[] = ['einweisung'];

/* Die Platzhalterkachel ist hier entfallen: seit der A+S-Plan ein eigenes
   Modul hat, steht hinter jeder Kachel dieser Kette ein gebautes Werkzeug.
   Fuer das SiFa-Raster wurde sie als AngekuendigtTile (ModuleTile.tsx) aus
   Stand 0858912 zurueckgeholt — bei Bedarf von dort nehmen, nicht neu erfinden. */

interface Props {
  vorankStats: ModuleStats;
  unterlageStats: ModuleStats;
  sigeplanStats: ModuleStats;
  asplanStats: ModuleStats;
  inspectionStats: ModuleStats;
  einweisungStats: ModuleStats;
  planungStats: ModuleStats;
  /**
   * Ampel der Planungsphase (usePlanungKachel, Stufe 1.5/1.6): Farbe,
   * Klaerungsquote, Gruende, ob eine Phase existiert. Fehlt der Stand, zeigt
   * die Kachel nur den LV-Untertitel wie bisher.
   */
  planung?: PlanungKachelStand;
  /**
   * Stand der Baustellenordnung (useBauordnungKachel + bauordnungKachelAnzeige,
   * Stufe B4): eine Zeile unter dem Titel der Kachel 06. Fehlt er, sieht die
   * Kachel aus wie bisher.
   */
  bauordnung?: BauordnungKachelAnzeige;
  onNavigate: (module: ModuleTarget) => void;
  onOpenEditor: () => void;
  currentInspectionNumber: number;
  reportExempt?: boolean;
  isReadOnly?: boolean;
  /**
   * Ist das Begehungs-Kontingent eine ANZAHL (Stueckpositionen) oder ein
   * Stundenbudget? Siehe isInspectionBudgetCountable. Bei Stunden zeigt die
   * Begehungskachel nur die durchgefuehrten Begehungen, ohne Nenner.
   */
  inspectionBudgetCountable?: boolean;
  /** Letzte Aktivitaet je Kachel (useModuleActivity); fehlende Schluessel zeigen „Noch keine Aktivität". */
  activity?: ModuleActivity;
  /**
   * Offene externe Freimeldungen des Projekts (§F). Ueber 0 ersetzt der
   * Zaehler das „LAUFEND" der Begehungskachel — im Projekt-Hub ist das die
   * einzige Stelle, an der jemand sieht, dass von aussen etwas hereinkam,
   * ohne das Begehungsmodul zu oeffnen. Ohne Migration 108 bleibt er 0 und
   * die Kachel sieht aus wie immer.
   */
  offeneFreimeldungen?: number;
  /**
   * Handy-App (docs/android-app-plan.md 1.4): welche Kacheln gezeigt werden.
   * Fehlt die Angabe, ist alles sichtbar wie bisher (Browser, Tablet-App).
   */
  modulSichtbar?: (kennung: HubModulKennung) => boolean;
}

export const ProjectModuleGrid: React.FC<Props> = ({
    vorankStats, unterlageStats, sigeplanStats, asplanStats, inspectionStats, einweisungStats, planungStats, planung, bauordnung, onNavigate, onOpenEditor, currentInspectionNumber, reportExempt = false, isReadOnly = false, inspectionBudgetCountable = true, activity = KEINE_AKTIVITAET, offeneFreimeldungen = 0, modulSichtbar
}) => {
    const isMobile = useIsMobile();
    // Ohne `modulSichtbar` ist jede Kachel sichtbar — Browser und Tablet-App wie bisher.
    const zeige = (kennung: HubModulKennung) => !modulSichtbar || modulSichtbar(kennung);
    // Ein Abschnitt ohne sichtbare Kachel entfaellt samt Ueberschrift.
    const verlaufLeer = (isMobile ? VERLAUF_KACHELN_MOBIL : VERLAUF_KACHELN).every((k) => !zeige(k));
    const freimeldungBadge = offeneFreimeldungen > 0
        ? `${offeneFreimeldungen} FREIMELDUNG${offeneFreimeldungen > 1 ? 'EN' : ''}`
        : undefined;

    return (
        <div className="space-y-6 animate-fade-in">

            {/* Die Ablage-Karte (Dokumente, Archiv, Sendeverlauf) ist mit dem
                SiFa-Raster gemeinsam und steht in ModuleTile.tsx. */}
            <AblageKarte onNavigate={onNavigate} activity={activity} isMobile={isMobile} modulSichtbar={modulSichtbar} />

            {/* ============================================================
                PROJEKTVERLAUF — die einmaligen Dokumente stehen in der
                Reihenfolge, in der sie anfallen (01 bis 05), und tragen ihre
                Schrittnummer fest: sie ruecken NICHT auf, wenn auf dem Handy
                Kacheln fehlen — die Ziffer benennt den Schritt, nicht den
                Platz im Raster.
                Die Begehung steht bewusst am Ende unten rechts und traegt
                statt einer Ziffer „LAUFEND": sie ist der einzige Punkt, der
                sich ueber die ganze Bauzeit wiederholt.
               ============================================================ */}
            {!verlaufLeer && (
            <section>
                <SectionHeading label="Projektverlauf" hint="Von der Planung bis zur Übergabe" />
                <div data-tutorial="hub.modulraster" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

                    {/* 01 Planungsphase — Schreibtischarbeit, deshalb bewusst
                        im `!isMobile`-Zweig: Baubeschreibung erfassen,
                        Fragenkatalog fuehren und Bedenkenanzeigen schreiben tut
                        niemand auf dem Handy; das Dashboard zeigt dafuer die
                        Liste „Planungsphase — Handlungsbedarf".
                        Vorn stehen Ampel und Klaerungsquote (Entscheid E12),
                        die LV-Menge (`planning_general` — die Zuordnung gab es
                        im LV-Katalog schon vor diesem Modul) wandert in den
                        Untertitel; der Zaehler oben rechts entfaellt deshalb.
                        Die Gruende der Ampel stehen im Tooltip. Hinweis 1.6
                        (Planungsleistung im LV, aber keine Phase) kommt aus
                        PlanungKachelHinweis — angelegt wird dabei nichts. */}
                    {!isMobile && zeige('planung') && (
                        <ModuleTile
                            accent="cyan"
                            step="01"
                            icon={<ClipboardList className="w-5 h-5 text-cyan-600" />}
                            title="Planungsphase"
                            status={planung ? <PlanungKachelStatus stand={planung} /> : undefined}
                            subtitle={planungUntertitel(planungStats, isReadOnly)}
                            notice={planung ? <PlanungKachelHinweis stand={planung} lvHatPlanungsleistung={lvHatPlanungsleistung(planungStats)} /> : undefined}
                            hint={planung?.ampel ? gruendeTooltip(planung.ampel) : undefined}
                            cta="Öffnen"
                            onClick={() => onNavigate('planung')}
                            lastActivity={activity.planung}
                        />
                    )}

                    {/* 02 Vorankündigung */}
                    {!isMobile && zeige('vorank') && (
                        <ModuleTile
                            accent="violet"
                            step="02"
                            icon={<Calendar className="w-5 h-5 text-violet-600" />}
                            title="Vorankündigung"
                            subtitle="Nach BaustellV (RAB 30)"
                            cta="Öffnen"
                            onClick={() => onNavigate('vorank')}
                            stats={vorankStats}
                            lastActivity={activity.vorank}
                        />
                    )}

                    {/* 03 SiGe-Plan — EINE Kachel fuer drei Werkzeuge.
                        Gewerke-Ueberschneidung und Handbuch hatten hier eigene
                        Kacheln; beide lesen aber nichts anderes als den Plan
                        (Zeitraeume, Gewerke, Gefaehrdungen) und stehen deshalb
                        jetzt als Reiter IN ihm (SigePlanModul). Der Untertitel
                        nennt sie ausdruecklich — sonst suchte man sie im
                        Raster, wo sie nicht mehr sind. */}
                    {!isMobile && zeige('sigeplan') && (
                        <ModuleTile
                            data-tutorial="hub.kachel-sigeplan"
                            accent="blue"
                            step="03"
                            icon={<Map className="w-5 h-5 text-blue-600" />}
                            title="SiGe-Plan"
                            subtitle="Plan, Gewerke-Überschneidung & SiGe-Handbuch"
                            cta="Öffnen"
                            onClick={() => onNavigate('sigeplan')}
                            stats={sigeplanStats}
                            lastActivity={activity.sigeplan}
                        />
                    )}

                    {/* A+S-Plan — steht hinter dem SiGe-Plan und vor dem
                        Handbuch: er ist kein Schritt der Regelfolge, sondern
                        ein eigener Plan, den es nur gibt, wenn in
                        kontaminierten Bereichen gearbeitet wird. Deshalb ein
                        BEDARF-Merkmal statt einer Schrittziffer — eine
                        durchnummerierte Kachel liesse ihn wie eine
                        vergessene Pflicht aussehen, wo er gar nicht anfaellt.

                        Eigener Farbton (stone): Schadstoffe sind nicht blau
                        wie der Plan und nicht fuchsia wie das Handbuch.
                        Schreibtischarbeit, deshalb `!isMobile` — Befunde,
                        Cluster und Schutzklassen pflegt niemand auf dem
                        Handy; die Aushaenge werden hier gedruckt.

                        Der Zaehler haengt an der LV-Zuordnung `asplan`. Sie
                        steht in KEINEM Standard-LV — ohne eine im LV-Editor
                        angelegte Position bleibt die Kachel ohne Zaehler,
                        und genau so soll es sein: der A+S-Plan wird
                        beauftragt oder er faellt nicht an.

                        Seit Plan Paketposition B5 BUCHT der A+S-Plan nach
                        der Freigabe auf diese Position. Geprueft, nicht
                        doppelt: der Zaehler (calculateStats ueber
                        istLeistungVomTyp) summiert `quantityUsed` der
                        `asplan`-Positionen — er zaehlt KEINE Freigaben. Die
                        neue Buchung ist also genau das, was er schon immer
                        zaehlte (vorher nur ueber die Schnellbuchung im Hub);
                        die Freigabe selbst geht nicht noch einmal ein. Der
                        Bestandteil „A+S-Plan" einer Paketposition hebt
                        `quantityUsed` nicht (nur die Paketbuchung bei 5/5,
                        auf der Paketposition selbst) — auch er zaehlt hier
                        nicht zusaetzlich. */}
                    {!isMobile && zeige('asplan') && (
                        <ModuleTile
                            accent="stone"
                            badge="BEI BEDARF"
                            icon={<Biohazard className="w-5 h-5 text-stone-600" />}
                            title="A+S-Plan"
                            subtitle="Kontaminierte Bereiche nach DGUV Regel 101-004"
                            cta="Öffnen"
                            onClick={() => onNavigate('asplan')}
                            stats={asplanStats}
                            lastActivity={activity.asplan}
                        />
                    )}

                    {/* 04 Einweisung SiGe-Plan — AUCH AUF DEM HANDY SICHTBAR.
                        Vorankuendigung, Unterlage und Archiv sind bewusst per
                        `!isMobile` ausgeblendet: das sind Schreibtisch-
                        dokumente. Die Einweisung ist das Gegenteil davon — sie
                        wird auf der Baustelle gehalten, am Handy, mit
                        Unterschriften der Anwesenden. Sie deshalb NICHT in
                        einen `!isMobile`-Zweig wandern lassen. */}
                    {zeige('einweisung') && (
                    <ModuleTile
                        accent="teal"
                        step="04"
                        icon={<ClipboardCheck className="w-5 h-5 text-teal-600" />}
                        title="Einweisung SiGe-Plan"
                        subtitle={isReadOnly ? 'Nur-Ansicht: ansehen & drucken' : 'Unterweisung auf der Baustelle'}
                        cta="Öffnen"
                        onClick={() => onNavigate('einweisung')}
                        stats={einweisungStats}
                        lastActivity={activity.einweisung}
                    />
                    )}

                    {/* 05 Unterlage (RAB 32) — das Schlussdokument */}
                    {!isMobile && zeige('unterlage') && (
                        <ModuleTile
                            accent="amber"
                            step="05"
                            icon={<FileSpreadsheet className="w-5 h-5 text-amber-600" />}
                            title="Unterlage (RAB 32)"
                            subtitle="Für spätere Arbeiten am Bauwerk"
                            cta="Öffnen"
                            onClick={() => onNavigate('unterlage')}
                            stats={unterlageStats}
                            lastActivity={activity.unterlage}
                        />
                    )}

                    {/* 06 Baustellenordnung & HSE-Plan — die LETZTE Kachel des
                        Rasters, aber VOR dem Begehungsblock. Die Begehung muss
                        das letzte Kind bleiben: sie traegt
                        `md:col-span-2 lg:col-span-1`, und diese Klassen wirken
                        nur so, wie sie gemeint sind, solange nichts hinter ihr
                        steht. Eine Kachel dahinter schoebe sie in die
                        Zeilenmitte.
                        Mit dieser achten Kachel ist die zweite Rasterzeile voll
                        und die Begehung sitzt auf grossen Bildschirmen
                        tatsaechlich in der vierten Spalte — die Einfuegung
                        stellt die beschriebene Anordnung her, statt sie zu
                        stoeren. Dass auf mittleren Bildschirmen daneben eine
                        Zelle frei bleibt, ist hinzunehmen und NICHT dadurch zu
                        beheben, dass die Begehung nach oben gezogen wird.

                        `!isMobile` wie bei 01, 02, 05: Kapitel pflegen und eine
                        Word-Datei erzeugen tut niemand auf dem Handy. Die
                        Ansicht dahinter sichert sich zusaetzlich selbst ab —
                        die Kachelbedingung verbirgt nur den Einstieg.

                        Ohne `stats`: der `site_rules`-Zaehler haengt noch am
                        Handbuch-Reiter im SiGe-Plan. Er wandert mit der Buchung
                        hierher (Baureihenfolge Schritt 10). Zwei Stellen duerfen
                        denselben Zaehler nicht gleichzeitig beanspruchen, und
                        eine erfundene „0 / 0" behauptete eine offene Aufgabe,
                        wo gar keine Leistung im LV steht. */}
                    {!isMobile && zeige('bauordnung') && (
                        <ModuleTile
                            accent="fuchsia"
                            step="06"
                            icon={<Building2 className="w-5 h-5 text-fuchsia-600" />}
                            title="Baustellenordnung"
                            status={bauordnung ? <BauordnungKachelStatus laden={bauordnung.laden} text={bauordnung.text?.text} ton={bauordnung.text?.ton} /> : undefined}
                            subtitle="Baustellenordnung & HSE-Plan"
                            cta="Öffnen"
                            onClick={() => onNavigate('bauordnung')}
                            lastActivity={activity.bauordnung}
                        />
                    )}

                    {/* Begehungsbericht — unten rechts. Auf dem Handy NICHT
                        hier: dort steht sie als Knopf ganz oben im Hub
                        (MobileBegehungButton), die Kachel entfaellt. */}
                    {!isMobile && zeige('module-sigeko-inspection') && (reportExempt ? (
                        <div title="Begehungen sind für dieses Projekt deaktiviert." className="bg-slate-50 p-5 pb-6 rounded-2xl border border-slate-200 relative overflow-hidden opacity-50 grayscale cursor-not-allowed flex flex-col md:col-span-2 lg:col-span-1">
                            <div className="bg-slate-200 w-10 h-10 rounded-xl flex items-center justify-center mb-3 border border-slate-300"><ShieldCheck className="w-5 h-5 text-slate-400" /></div>
                            <span className="text-[10px] font-black tracking-[0.16em] text-slate-300 block mb-0.5">LAUFEND</span>
                            <h3 className="font-bold text-slate-600 text-sm relative z-10">Begehungsbericht</h3>
                            <p className="text-[11px] text-slate-400 mt-0.5 mb-2 relative z-10 flex-1">Für dieses Projekt deaktiviert</p>
                            <ActivityLine value={activity.begehung} label="Letzte Begehung" className="mb-3" />
                            <div className="flex items-center text-slate-400 text-[11px] font-bold relative z-10 cursor-not-allowed">Deaktiviert</div>
                        </div>
                    ) : (
                        /* Im Lesemodus bleibt die Kachel anklickbar: der Dialog
                           dahinter fuehrt auch zum reinen Ansehen des laufenden
                           Berichts. Nur das Anlegen der naechsten Begehung ist
                           gesperrt (useReportActions), deshalb sagt die Kachel
                           hier „Ansehen" statt „Starten". */
                        <ModuleTile
                            data-tutorial="hub.kachel-begehung"
                            accent="emerald"
                            /* Der Freimelde-Zaehler verdraengt „LAUFEND", solange
                               etwas offen ist: eine zweite Zeile gibt die Kachel
                               nicht her, und „laufend" ist die Begehung ohnehin
                               immer — die Meldung von aussen ist die Nachricht. */
                            badge={freimeldungBadge ?? 'LAUFEND'}
                            badgeTone={freimeldungBadge ? 'alarm' : 'accent'}
                            icon={<ShieldCheck className="w-5 h-5 text-emerald-600" />}
                            title="Begehungsbericht"
                            subtitle={isReadOnly ? 'Nur-Ansicht: keine neue Begehung' : `Nächster Bericht: Nr. ${currentInspectionNumber}`}
                            cta={isReadOnly ? 'Ansehen' : 'Starten'}
                            onClick={onOpenEditor}
                            stats={inspectionStats}
                            usedOverride={Math.max(0, currentInspectionNumber - 1)}
                            countOnly={!inspectionBudgetCountable}
                            hint={freimeldungBadge
                                ? (offeneFreimeldungen === 1
                                    ? 'Ein Mangel wurde online als behoben gemeldet und wartet auf Ihre Entscheidung.'
                                    : `${offeneFreimeldungen} Mängel wurden online als behoben gemeldet und warten auf Ihre Entscheidung.`)
                                : isReadOnly ? 'Nur-Ansicht: Der Bericht lässt sich ansehen, eine neue Begehung wird nicht angelegt.' : undefined}
                            className="md:col-span-2 lg:col-span-1"
                            lastActivity={activity.begehung}
                            activityLabel="Letzte Begehung"
                        />
                    ))}

                </div>
            </section>
            )}
        </div>
    );
};
